/**
 * DOM view for the vitals hero (DIRECTION.md §1.1, `design/revamp/`) —
 * replaces `SummaryStripPanel`'s one-line strip with a full-width band: a
 * status pill (Awake/Sleeping/Stalled/Unknown), a large NAV number with an
 * inline equity sparkline, day/unrealized/realized P&L, positions + open
 * orders, last decision, and data freshness. Renders `vitals.ts`'s pure
 * `buildVitalsState()` output and stays live on the same poll cadence every
 * other panel uses.
 */

import type { RoleSnapshot } from "../data/snapshotIndex";
import type { LogEvent } from "../types/events";
import type { EquityCurveRow } from "../types/snapshot";
import type { FleetOrganismEntry, PerformanceDocument, SizingDocument } from "../types/incremental";
import { loadFleetSnapshot } from "../data/fleet";
import { isIncrementalIndex } from "../types/incremental";
import {
  buildVitalsState,
  formatPositionsAndOrders,
  type VitalsState,
  type VitalsStatus,
} from "./vitals";
import { fetchSizingSafely, sizingFingerprint } from "./sizingView";
import { Poller, freshnessBadgeLabel, freshnessLabel } from "../data/polling";
import { formatMoney, formatPercent, formatSignedMoney, toneClass } from "../format/number";
import { renderSparklineSvg } from "../charts/sparklineElement";
import { isNotPublishedYet } from "../data/fetchJson";
import { config } from "../config";
import { attachTooltip } from "../ui/tooltip";

interface Snapshot {
  readonly events: readonly LogEvent[];
  readonly performance: PerformanceDocument | null;
  readonly equityCurve: readonly EquityCurveRow[];
  readonly sizing: SizingDocument | null;
  /** OQA: this organism's fleet.json row (unit state), `null` if unavailable. */
  readonly fleetEntry: FleetOrganismEntry | null;
}

const STATUS_LABEL: Readonly<Record<VitalsStatus, string>> = {
  awake: "Awake",
  sleeping: "Sleeping",
  stalled: "Stalled",
  stopped: "Stopped",
  unknown: "Status unknown",
};

/** `VitalsStatus` -> the `.obs-alive-*` dot-colour modifier (`style.css`) —
 * a lookup table, matching this file's own established pattern (rather
 * than a nested nested ternary, code-review finding round 2). */
const STATUS_DOT_TONE: Readonly<Record<VitalsStatus, string>> = {
  awake: "awake",
  sleeping: "asleep",
  stalled: "stalled",
  stopped: "unknown",
  unknown: "unknown",
};

const STATUS_TOOLTIP: Readonly<Record<VitalsStatus, string>> = {
  awake: "A brain.wake or runtime.heartbeat has landed within the last two decision cycles.",
  sleeping: "The market/session is closed, or a compute request was rejected — quiet is expected.",
  stalled:
    "The session says this organism should be awake, but neither a brain.wake nor a " +
    "runtime.heartbeat has landed in over two decision cycles — worth checking.",
  stopped:
    "The runtime unit is stopped (fleet.json unit_state) — nothing here is live; the figures " +
    "are its last published state.",
  unknown: "No session, wake, or heartbeat evidence exists yet to judge either way.",
};

const SPARK_WIDTH = 96;
const SPARK_HEIGHT = 32;

export class VitalsHero {
  private poller: Poller<Snapshot> | null = null;
  private freshnessEl: HTMLSpanElement | null = null;
  // See SummaryStripPanel's identical guard: `mount()` is fire-and-forget
  // from OrganismScreen (must never block Brain/Market/Log's critical
  // path), so a real dispose() can land while the initial fetch is still in
  // flight.
  private disposed = false;

  constructor(
    private readonly container: HTMLElement,
    private readonly role: RoleSnapshot,
  ) {}

  async mount(): Promise<void> {
    this.container.innerHTML = "";
    this.container.className = "obs-vitals-hero";

    const initial = await this.fetchSnapshot();
    if (this.disposed) return;
    this.render(initial);

    this.poller = new Poller<Snapshot>(
      async () => {
        const snapshot = await this.fetchSnapshot();
        const key =
          `${snapshot.events.length}:${snapshot.performance?.as_of_seq ?? "none"}:` +
          `${snapshot.equityCurve.length}:${sizingFingerprint(snapshot.sizing)}:` +
          `${snapshot.fleetEntry?.unit_state ?? "?"}`;
        return { key, value: snapshot };
      },
      (pollSnapshot) => {
        if (pollSnapshot.value && pollSnapshot.status !== "unchanged") {
          this.render(pollSnapshot.value);
        }
        if (this.freshnessEl) {
          this.freshnessEl.textContent = freshnessBadgeLabel(
            pollSnapshot.status,
            pollSnapshot.lastSuccessAt,
          );
        }
      },
      { intervalMs: config.pollIntervalMs, backoffMaxMs: config.pollBackoffMaxMs },
      isNotPublishedYet,
    );
    this.poller.start();
  }

  private async fetchSnapshot(): Promise<Snapshot> {
    // `fetchSizingSafely` (code review finding) isolates a sizing-only fetch
    // failure to a `null` result — without it, a rejected `role.sizing()`
    // promise would fail this WHOLE `Promise.all`, blanking NAV/P&L/
    // positions/last-decision too, none of which have anything to do with
    // sizing.
    const [events, performance, equityCurve, sizing, fleetEntry] = await Promise.all([
      this.role.events(),
      this.role.performance(),
      this.role.equityCurve(),
      fetchSizingSafely(this.role),
      this.fetchFleetEntry(),
    ]);
    return { events, performance, equityCurve, sizing, fleetEntry };
  }

  /** OQA: the organism's fleet.json row — the only published source of whether
   * its unit is running. Failure-isolated like sizing: never blanks the hero. */
  private async fetchFleetEntry(): Promise<FleetOrganismEntry | null> {
    try {
      const fleet = await loadFleetSnapshot();
      return (
        fleet?.organisms.find((o) => o.organism_id === this.role.location.organismId) ??
        fleet?.organisms.find((o) => o.role === this.role.location.role) ??
        null
      );
    } catch (error) {
      console.warn("observatory: fleet.json fetch failed; status from events alone", error);
      return null;
    }
  }

  private render(snapshot: Snapshot): void {
    this.container.innerHTML = "";
    const state = buildVitalsState(
      snapshot.events,
      snapshot.performance,
      snapshot.equityCurve,
      this.role.location.role,
      {
        sizing: snapshot.sizing,
        unitState: snapshot.fleetEntry?.unit_state ?? null,
        freshEpisode:
          isIncrementalIndex(this.role.index) &&
          this.role.index.episode?.status === "pending_first_wake",
      },
    );

    const top = document.createElement("div");
    top.className = "obs-vitals-top";
    top.appendChild(this.statusPill(state));
    top.appendChild(this.navBlock(state));
    this.container.appendChild(top);

    this.container.appendChild(this.statGrid(state));
  }

  private statusPill(state: VitalsState): HTMLDivElement {
    const pill = document.createElement("div");
    pill.className = `obs-vitals-status-pill obs-status-${state.status}`;
    const dot = document.createElement("span");
    dot.className = `obs-vitals-status-dot obs-alive-${STATUS_DOT_TONE[state.status]}`;
    const label = document.createElement("span");
    // Show the real session label whenever it's known (closed/pre-market/
    // after-hours/overnight/regular session) — not just while status is
    // "awake". The deleted SummaryStripPanel got this right; an earlier
    // draft of this hero only showed it for "awake", which meant a
    // "Sleeping" pill could no longer tell a reader closed from pre-market
    // from after-hours (code-review finding, revamp self-review round 2).
    label.textContent =
      state.status === "stopped"
        ? state.freshEpisode
          ? "Stopped · fresh episode awaiting first wake"
          : STATUS_LABEL.stopped
        : state.sessionLabel === "unknown"
          ? STATUS_LABEL[state.status]
          : `${STATUS_LABEL[state.status]} · ${state.sessionLabel}`;
    pill.appendChild(dot);
    pill.appendChild(label);
    attachTooltip(pill, STATUS_TOOLTIP[state.status]);
    return pill;
  }

  private navBlock(state: VitalsState): HTMLDivElement {
    const block = document.createElement("div");
    block.className = "obs-vitals-nav-block";
    block.style.flex = "1";

    const label = document.createElement("div");
    label.className = "obs-vitals-nav-label";
    label.textContent = "Total value (NAV)";
    block.appendChild(label);

    const row = document.createElement("div");
    row.className = "obs-vitals-nav-row";

    const value = document.createElement("span");
    value.className = "obs-vitals-nav-value obs-num-display";
    value.textContent = formatMoney(state.nav);
    row.appendChild(value);

    const spark = this.sparkline(state.navSeries);
    if (spark) row.appendChild(spark);

    block.appendChild(row);

    const pnl = document.createElement("div");
    pnl.className = "obs-vitals-pnl";
    const entries: { readonly label: string; readonly raw: string | number | null }[] = [
      { label: "day", raw: state.dayPnl },
      { label: "unrealized", raw: state.unrealizedPnl },
      { label: "realized (lifetime)", raw: state.realizedPnlCum },
    ];
    for (const entry of entries) {
      if (entry.raw === null) continue;
      const result = formatSignedMoney(entry.raw);
      const span = document.createElement("span");
      span.className = toneClass(result.tone);
      span.textContent = `${result.text} ${entry.label}`;
      pnl.appendChild(span);
    }
    attachTooltip(
      pnl,
      "Day P&L is derived from this organism's equity curve (latest NAV minus its first row of " +
        "the current UTC day) — unrealized/realized P&L read straight from the latest performance snapshot.",
    );
    block.appendChild(pnl);

    return block;
  }

  private sparkline(series: readonly number[]): SVGElement | null {
    const svg = renderSparklineSvg(series, {
      width: SPARK_WIDTH,
      height: SPARK_HEIGHT,
      padding: 3,
      endDot: true,
      tooltip: `NAV over the last ${series.length} recorded cycles, from this organism's equity curve.`,
    });
    svg?.setAttribute("class", "obs-vitals-sparkline");
    return svg;
  }

  private statGrid(state: VitalsState): HTMLDivElement {
    const grid = document.createElement("div");
    grid.className = "obs-vitals-grid";
    grid.appendChild(this.positionsStat(state));
    const exposure = this.grossExposureStat(state);
    if (exposure) grid.appendChild(exposure);
    grid.appendChild(this.lastDecisionStat(state));
    grid.appendChild(this.freshnessStat());
    return grid;
  }

  private stat(labelText: string): { root: HTMLDivElement; value: HTMLSpanElement } {
    const root = document.createElement("div");
    root.className = "obs-vitals-stat";
    const label = document.createElement("span");
    label.className = "obs-vitals-stat-label";
    label.textContent = labelText;
    const value = document.createElement("span");
    value.className = "obs-vitals-stat-value";
    root.appendChild(label);
    root.appendChild(value);
    return { root, value };
  }

  private positionsStat(state: VitalsState): HTMLDivElement {
    const { root, value } = this.stat("Positions & orders");
    // Plain words, not a bare "1, 0" (2026-09-28 fix — verified live: the
    // old comma-joined pair of bare numbers read as a typo, not a
    // sentence). `formatPositionsAndOrders` (vitals.ts) is the one place
    // this phrasing lives, so any other screen that needs it stays in sync.
    value.textContent = formatPositionsAndOrders(state.openPositions, state.openOrders);
    attachTooltip(root, "Open positions and open orders from the latest portfolio.snapshot event.");
    return root;
  }

  /** R3 ("observatory visibility of smoother state") — the book's total
   * gross exposure against its cap, from `index.json.refs.sizing`. `null`
   * entirely (no stat cell at all, not a "–" placeholder) until this
   * organism has recorded at least one `sizing.smoother_state` event —
   * mirrors `SizingPanel`'s own honest-empty-state rule. */
  private grossExposureStat(state: VitalsState): HTMLDivElement | null {
    if (state.grossExposure === null || state.grossExposureCap === null) return null;
    const { root, value } = this.stat("Gross exposure");
    value.textContent = `${formatPercent(state.grossExposure)} of ${formatPercent(state.grossExposureCap, 0)}`;
    attachTooltip(
      root,
      "Sum of every attended symbol's own marked notional, divided by NAV, against the whole-book " +
        "gross-exposure cap — from the Positions & sizing panel below.",
    );
    return root;
  }

  private lastDecisionStat(state: VitalsState): HTMLDivElement {
    const { root, value } = this.stat("Last decision");
    if (state.lastDecisionEventTime) {
      const ms = Date.parse(state.lastDecisionEventTime);
      const relative = Number.isFinite(ms) ? freshnessLabel(ms) : state.lastDecisionEventTime;
      value.textContent = state.lastDecisionMode
        ? `${relative} · ${state.lastDecisionMode}`
        : relative;
    } else {
      value.textContent = "no wake recorded yet";
    }
    attachTooltip(
      root,
      "Time since the organism's most recent brain.wake event, and its mode (trade/observe/consolidate).",
    );
    return root;
  }

  private freshnessStat(): HTMLDivElement {
    const { root, value } = this.stat("Data");
    value.textContent = "live";
    value.classList.add("obs-freshness");
    this.freshnessEl = value;
    attachTooltip(
      root,
      "How long ago this page last successfully polled the organism's published snapshot.",
    );
    return root;
  }

  dispose(): void {
    this.disposed = true;
    this.poller?.stop();
  }
}
